import React, { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TournamentPromoOverlay from "../components/TournamentPromoOverlay";
import socket from "../lib/socket";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0));
}

function useTransparentBody() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlBackground: html.style.background,
      htmlBackgroundImage: html.style.backgroundImage,
      bodyBackground: body.style.background,
      bodyBackgroundImage: body.style.backgroundImage,
      bodyMargin: body.style.margin,
      bodyOverflow: body.style.overflow,
    };

    html.style.background = "transparent";
    html.style.backgroundImage = "none";
    body.style.background = "transparent";
    body.style.backgroundImage = "none";
    body.style.margin = "0";
    body.style.overflow = "hidden";

    return () => {
      html.style.background = previous.htmlBackground;
      html.style.backgroundImage = previous.htmlBackgroundImage;
      body.style.background = previous.bodyBackground;
      body.style.backgroundImage = previous.bodyBackgroundImage;
      body.style.margin = previous.bodyMargin;
      body.style.overflow = previous.bodyOverflow;
    };
  }, []);
}

function eventBoolean(event, key, fallback = true) {
  if (typeof event?.detail === "boolean") return event.detail;
  if (typeof event?.detail?.[key] === "boolean") return event.detail[key];
  return fallback;
}

function normalizeChannel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 80);
}

function useObsScenePromoSignal(channelId) {
  useEffect(() => {
    const channel = normalizeChannel(channelId);
    const isObsBrowserSource = Boolean(window.obsstudio);
    if (!channel || !isObsBrowserSource) return undefined;

    let sourceActive = true;
    let sourceVisible = true;
    let lastPublished = null;

    const publish = (force = false) => {
      if (!socket.connected) return;
      const active = sourceActive && sourceVisible;
      if (!force && active === lastPublished) return;

      socket.emit("tournament-promo:source-state", {
        channel,
        active,
        source: "obs-browser",
      });
      lastPublished = active;
    };

    const onConnect = () => publish(true);
    const onActiveChange = (event) => {
      sourceActive = eventBoolean(event, "active");
      publish();
    };
    const onVisibleChange = (event) => {
      sourceVisible = eventBoolean(event, "visible");
      publish();
    };

    socket.on("connect", onConnect);
    window.addEventListener("obsSourceActiveChanged", onActiveChange);
    window.addEventListener("obsSourceVisibleChanged", onVisibleChange);

    if (!socket.connected) socket.connect();
    else publish(true);

    return () => {
      window.removeEventListener("obsSourceActiveChanged", onActiveChange);
      window.removeEventListener("obsSourceVisibleChanged", onVisibleChange);
      socket.off("connect", onConnect);

      if (socket.connected) {
        socket.emit("tournament-promo:source-state", {
          channel,
          active: false,
          source: "obs-browser",
        });
      }
    };
  }, [channelId]);
}

export default function OverlayTournamentPromo() {
  const { channelId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const scale = clamp(searchParams.get("scale") || 1, 0.65, 1.4);
  const tournamentId = searchParams.get("tournament") || "get-active-3v3-75-ovr-2026";

  useTransparentBody();
  useObsScenePromoSignal(channelId);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        background: "transparent",
        transform: `scale(${scale})`,
        transformOrigin: "center",
      }}
    >
      <TournamentPromoOverlay mode="obs" clickable={false} tournamentId={tournamentId} />
    </div>
  );
}
